import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useMemo, useState } from 'react';
import { GitFork, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useDeleteFlow,
  useFlowsList,
  usePauseFlow,
  useResumeFlow,
} from '@/hooks/vsl-dinamica/useDynamicFlows';
import type { FlowListItem } from '@/services/dynamicFlowsService';
import { generateFlowEmbedCode } from '@/utils/vsl-dinamica/embed';
import { CreateFlowDialog } from '@/components/vsl-dinamica/list/CreateFlowDialog';
import { DeleteFlowDialog } from '@/components/vsl-dinamica/list/DeleteFlowDialog';
import { EmptyFlows, NoFlowsMatch } from '@/components/vsl-dinamica/list/EmptyFlows';
import { FlowCard } from '@/components/vsl-dinamica/list/FlowCard';
import { HowItWorksDialog } from '@/components/vsl-dinamica/list/HowItWorksDialog';
import { RenameFlowDialog } from '@/components/vsl-dinamica/list/RenameFlowDialog';
import {
  STATUS_FILTERS,
  countByStatus,
  filterFlows,
  type StatusFilter,
} from '@/components/vsl-dinamica/list/flowListUtils';

const errorMessage = (error: unknown, fallback: string) => (error as Error)?.message || fallback;

export default function FlowsListPage() {
  useLanguage();
  const { data: flows = [], isLoading, isError, refetch, isRefetching } = useFlowsList();
  const pauseFlow = usePauseFlow();
  const resumeFlow = useResumeFlow();
  const deleteFlow = useDeleteFlow();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [creating, setCreating] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [renaming, setRenaming] = useState<FlowListItem | null>(null);
  const [deleting, setDeleting] = useState<FlowListItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const counts = useMemo(() => countByStatus(flows), [flows]);
  const visible = useMemo(() => filterFlows(flows, query, status), [flows, query, status]);

  const copyCode = async (flow: FlowListItem) => {
    try {
      await navigator.clipboard.writeText(generateFlowEmbedCode(flow.public_id));
      if (flow.status === 'published') {
        toast.success(translateInterfaceText("Código copiado. Cole na página onde o vídeo deve aparecer."));
      } else {
        toast.warning(
          flow.status === 'draft'
            ? translateInterfaceText("Código copiado. Ele só mostra o vídeo depois que o fluxo for publicado.")
            : translateInterfaceText("Código copiado. O fluxo está pausado: o vídeo só aparece quando ele for retomado."),
        );
      }
    } catch {
      toast.error(translateInterfaceText("Não foi possível copiar. Copie o código pelo editor do fluxo."));
    }
  };

  const runOnFlow = async (
    flow: FlowListItem,
    action: () => Promise<unknown>,
    success: string,
    fallback: string,
  ) => {
    setBusyId(flow.id);
    try {
      await action();
      toast.success(success);
    } catch (error) {
      toast.error(errorMessage(error, fallback));
    } finally {
      setBusyId(null);
    }
  };

  const pause = (flow: FlowListItem) =>
    runOnFlow(
      flow,
      () => pauseFlow.mutateAsync(flow.id),
      'Fluxo pausado.',
      translateInterfaceText("Não foi possível pausar o fluxo."),
    );

  const resume = (flow: FlowListItem) =>
    runOnFlow(
      flow,
      () => resumeFlow.mutateAsync(flow.id),
      'Fluxo retomado.',
      translateInterfaceText("Não foi possível retomar o fluxo."),
    );

  const confirmDelete = async (id: string) => {
    try {
      await deleteFlow.mutateAsync(id);
      toast.success(translateInterfaceText("Fluxo excluído."));
      setDeleting(null);
    } catch (error) {
      toast.error(errorMessage(error, translateInterfaceText("Não foi possível excluir o fluxo.")));
    }
  };

  const clearFilters = () => {
    setQuery('');
    setStatus('all');
  };

  // Os diálogos ficam fora do Layout: ele monta os filhos duas vezes (móvel e desktop)
  // e o portal do Radix abriria dois diálogos.
  return (
    <>
      <Layout fixedSidebar>
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Cabeçalho no padrão das telas de recurso (Teste A/B, Hook Track): ícone, título e separador. */}
          <div className="flex-shrink-0 bg-transparent" data-page-header>
            <div className="mx-auto w-full max-w-[1600px] px-4 pb-4 pt-4 md:px-8 md:pb-5">
              <div className="flex items-center gap-2.5">
                <GitFork className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} aria-hidden="true" />
                <h1 className="text-[20px] font-semibold leading-[1.2] tracking-tight text-[#1E2330] md:text-[22px]">
                  {translateInterfaceText("VSL Dinâmica")}</h1>
              </div>
            </div>
            <div
              data-testid="page-header-separator"
              className="hidden h-px w-full lg:block"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
            />
          </div>

          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto flex max-w-[1600px] flex-col gap-[22px] px-4 pb-6 pt-6 sm:px-6">
              {isLoading ? (
                <div
                  className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-4"
                  aria-busy="true"
                >
                  {[0, 1, 2].map((key) => (
                    <Skeleton key={key} className="h-[260px] rounded-2xl" />
                  ))}
                </div>
              ) : isError ? (
                <section className="flex flex-col items-center gap-3 rounded-2xl border border-[#D9E0EA] bg-white px-6 py-12 text-center">
                  <p className="text-sm text-[#4B5563]">{translateInterfaceText("Não foi possível carregar seus fluxos.")}</p>
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    disabled={isRefetching}
                    onClick={() => refetch()}
                  >
                    {translateInterfaceText("Tentar novamente")}</Button>
                </section>
              ) : flows.length === 0 ? (
                <EmptyFlows onCreate={() => setCreating(true)} onHowItWorks={() => setExplaining(true)} />
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="flex h-[42px] max-w-[380px] flex-[1_1_280px] items-center gap-2 rounded-xl border border-[#D9E0EA] bg-white px-3 text-[#6B7280] focus-within:ring-2 focus-within:ring-[#0057FF]/30">
                      <Search className="h-4 w-4 flex-none" aria-hidden="true" />
                      <span className="sr-only">{translateInterfaceText("Buscar fluxo")}</span>
                      <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={translateInterfaceText("Buscar fluxo pelo nome")}
                        className="min-w-0 flex-1 bg-transparent text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF]"
                      />
                    </label>
                    <div
                      role="group"
                      aria-label={translateInterfaceText("Filtrar por status")}
                      className="flex flex-wrap gap-1 rounded-xl border border-[#D9E0EA] bg-white p-1"
                    >
                      {STATUS_FILTERS.map((filter) => (
                        <button
                          key={filter.value}
                          type="button"
                          aria-pressed={status === filter.value}
                          onClick={() => setStatus(filter.value)}
                          className={cn(
                            'h-[34px] rounded-[9px] px-3.5 text-[13px] transition-colors',
                            status === filter.value
                              ? 'bg-[#EEF4FF] font-semibold text-[#0057FF]'
                              : 'font-medium text-[#4B5563] hover:bg-[#F4F6FA]',
                          )}
                        >
                          {filter.label} · {counts[filter.value]}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 lg:ml-auto">
                      <Button
                        variant="outline"
                        onClick={() => setExplaining(true)}
                        className="min-h-[42px] px-5 hover:bg-[#F4F6FA] hover:text-[#111827]"
                      >
                        {translateInterfaceText("Como funciona?")}</Button>
                      <Button
                        onClick={() => setCreating(true)}
                        className="flex min-h-[42px] items-center gap-2 px-5"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {translateInterfaceText("Criar fluxo")}</Button>
                    </div>
                  </div>

                  {visible.length === 0 ? (
                    <NoFlowsMatch onClear={clearFilters} />
                  ) : (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-4">
                      {visible.map((flow) => (
                        <FlowCard
                          key={flow.id}
                          flow={flow}
                          busy={busyId === flow.id}
                          onCopyCode={copyCode}
                          onRename={setRenaming}
                          onPause={pause}
                          onResume={resume}
                          onDelete={setDeleting}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </Layout>

      <CreateFlowDialog open={creating} onOpenChange={setCreating} />
      <HowItWorksDialog open={explaining} onOpenChange={setExplaining} />
      <RenameFlowDialog flow={renaming} onOpenChange={(open) => !open && setRenaming(null)} />
      <DeleteFlowDialog
        flow={deleting}
        pending={deleteFlow.isPending}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
